/* eslint-disable @typescript-eslint/naming-convention */
import type { TextBoxBlock } from '@ps/web-martech-cms-types/Form';
import { Input } from '@ps/web-shared-components';
import type { ValidatorTranslation } from '@ps/web-shared-validators';
import { required } from '@ps/web-shared-validators';
import React, { useCallback } from 'react';

// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import formStyles from '../CMSForm.scss';
import { getErrorMessage } from '../utils';

import { TXTCLI_Required } from './TextBox.messages.json';
import styles from './TextBox.scss';

const numberExp = new RegExp(/\d/);
const symbolExp = new RegExp(/\W+/g);

type TextBoxType = 'text' | 'textarea' | 'hidden' | 'number';

const TextBox: React.FC<TextBoxBlock['text_box']> = ({
  type,
  label,
  hide_label,
  default_value,
  field_name,
  required_validation,
  min_length_validation,
  max_length_validation,
  illegal_characters_validation
}) => {
  const minLength = (min: number, message?: ValidatorTranslation) => (value: string) => ({
    valid: !value || value?.length >= min,
    message
  });

  const maxLength = (max: number, message?: ValidatorTranslation) => (value: string) => ({
    valid: !value || value?.length <= max,
    message
  });

  const illegalCharacters =
    (regExp: RegExp, message?: ValidatorTranslation) => (value: string) => ({
      valid: !regExp.test(value),
      message
    });

  const getValidators = useCallback(() => {
    const validators = [];
    if (required_validation.active) {
      const errorMessage = getErrorMessage(required_validation, TXTCLI_Required);
      validators.push(required(errorMessage));
    }

    if (min_length_validation.min_length) {
      const errorMessage = getErrorMessage(min_length_validation);
      validators.push(minLength(min_length_validation.min_length, errorMessage));
    }

    if (max_length_validation.max_length) {
      const errorMessage = getErrorMessage(max_length_validation);
      validators.push(maxLength(max_length_validation.max_length, errorMessage));
    }

    if (!illegal_characters_validation.symbols) {
      const errorMessage = getErrorMessage(illegal_characters_validation);
      validators.push(illegalCharacters(symbolExp, errorMessage));
    }

    if (!illegal_characters_validation.numbers) {
      const errorMessage = getErrorMessage(illegal_characters_validation);
      validators.push(illegalCharacters(numberExp, errorMessage));
    }

    return validators;
  }, [
    required_validation,
    min_length_validation,
    max_length_validation,
    illegal_characters_validation
  ]);

  return (
    <Input
      id={field_name}
      className={styles.textBox}
      label={label}
      hideLabel={hide_label || type === 'hidden'}
      labelContent={
        required_validation.active && <span className={formStyles.requiredIndicator}>*</span>
      }
      validators={getValidators()}
      type={type as TextBoxType}
      value={default_value}
    />
  );
};
export default TextBox;
